import React, { useContext, useEffect } from 'react';
import { createPortal } from 'react-dom';
import getScrollBarSize from '~/utilities/getScrollBarSize';
import isServerSideRendering from '~/utilities/isServerSideRendering';
import PortalContext from './PortalContext';

type OwnProps = React.PropsWithChildren<{
  id: string;
  open: boolean;
  target?: HTMLElement;
}>;

export function useBodyScrollLock(isOpen: boolean) {
  useEffect(() => {
    const body = document.body;

    if (isOpen) {
      const size = getScrollBarSize();

      body.classList.add('no-scroll', 'm:pr-[var(--scrollbar-width)]');
      body.style.setProperty('--scrollbar-width', `${size}px`);

      return;
    }

    body.classList.remove('no-scroll', 'm:pr-[var(--scrollbar-width)]');
    body.style.removeProperty('--scrollbar-width');
  }, [isOpen]);
}

export default function Portal({
  children,
  id,
  open,
  target = isServerSideRendering
    ? undefined
    : document.querySelector('main') ?? document.body
}: OwnProps) {
  const { closePortal, hasOpenPortals, openPortal } = useContext(PortalContext);
  const toggle = open ? openPortal : closePortal;

  useEffect(() => {
    toggle(id);
  }, [id, toggle]);

  useBodyScrollLock(hasOpenPortals);

  // Remove no-scroll when Portal unmounts in the case Portal is wrapped in an error boundary
  useEffect(
    () => () => {
      document.body.classList.remove('no-scroll');
    },
    []
  );

  return open && target ? createPortal(children, target) : null;
}
